import type { PopperProps } from "@mui/base";
import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { notEmpty } from "shared/lib/utils";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";

import { useDebouncedAutoComplete } from "./hooks";
import {
  Container,
  FullWidthBackground,
  StyledInput as input,
  StyledAutocomplete,
  StyledBaseInput,
  StyledClose,
  StyledInputAdornment,
  StyledMagnifier,
} from "./styles";

export type Props = {
  className?: string;
  onChange?: (value: string) => void;
};

export const SearchInput: FunctionComponent<Props> = ({ className, onChange }) => {
  const { currentValue, searchRouteFunction } = useSearchContext();
  const router = useRouter();

  // Prevent it will refresh the search result when the user type in the input box
  const [committedSearchTerm, setCommittedSearchTerm] = useState<null | string>(currentValue);
  const [searchInput, setSearchInput] = useState("");
  const { fetchSuggestions, resetSuggestions, suggestionsState } = useDebouncedAutoComplete();
  const suggestions = suggestionsState.result;

  useEffect(() => {
    async function fetchData() {
      await fetchSuggestions(searchInput);
    }
    void fetchData();
  }, [fetchSuggestions, searchInput]);

  const handleSearch = useCallback(
    (value: null | string) => {
      if (!notEmpty(value)) return;
      const searchPageRoute = searchRouteFunction?.({ q: value });
      if (!searchPageRoute) return;

      const { asPath, ...restOfSearchPageRoute } = searchPageRoute;
      // The ?q=${query.q} is for trace easier to retrieve the trending search keyword
      void router.push(restOfSearchPageRoute, `${asPath}?q=${value}`);

      sendGA4Tracking({
        action: "sys",
        category: "search",
        customized_parameters: {
          search_text: value,
        },
        subcategory: "result",
      } as SearchEvent);
    },
    [router, searchRouteFunction],
  );

  const handleReset = useCallback(() => {
    setSearchInput("");
    setCommittedSearchTerm("");
    resetSuggestions();
    const searchPageRoute = searchRouteFunction?.({ value: "" });
    if (!searchPageRoute) return;

    const { asPath, ...restOfSearchPageRoute } = searchPageRoute;
    void router.push(restOfSearchPageRoute, asPath);
  }, [router, searchRouteFunction, resetSuggestions]);

  const isShowCloseButton = useMemo(
    () => notEmpty(currentValue) && committedSearchTerm,
    [currentValue, committedSearchTerm],
  );

  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  const popperModifiers = useMemo<PopperProps["modifiers"]>(
    () => [
      {
        name: "flip",
        options: {
          fallbackPlacements: [],
        },
      },
      {
        enabled: true,
        fn: ({ state }) => {
          state.elements.popper.style.inlineSize = "calc(100% + 20px)";
        },
        name: "customInlineSize",
        phase: "afterWrite",
      },
    ],
    [],
  );

  const renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement> & { key?: number | string },
      option: unknown,
      { inputValue }: { inputValue: string },
    ) => {
      const { key, ...restProps } = props;
      if (typeof option !== "string") return null;
      const parts = option.split(new RegExp(`(${inputValue})`, "ig"));
      return (
        <li key={key} {...restProps}>
          <div>
            {parts.map((part, index) =>
              part.toLowerCase() === inputValue.toLowerCase() ? (
                <strong key={index}>{part}</strong>
              ) : (
                <span key={index}>{part}</span>
              ),
            )}
          </div>
        </li>
      );
    },
    [],
  );

  useEffect(() => {
    const queryParameter = router.query.q as string | undefined;
    if (queryParameter === undefined) {
      setSearchInput("");
      setCommittedSearchTerm("");
    } else {
      setSearchInput(queryParameter);
      setCommittedSearchTerm(queryParameter);
    }
  }, [router.query.q]);

  return (
    <Container $stickyStackedHeight={appBarStickyHeight} className={className}>
      <FullWidthBackground />
      <StyledAutocomplete
        disablePortal
        freeSolo
        onChange={(event, value) => {
          const newValue = value as null | string;
          setCommittedSearchTerm(newValue);
          handleSearch(newValue);
        }}
        onInputChange={(event, newInputValue) => {
          onChange?.(newInputValue);
          setSearchInput(newInputValue);
        }}
        options={suggestions}
        renderInput={parameters => (
          <StyledBaseInput
            {...parameters.InputProps}
            endAdornment={
              <StyledInputAdornment>
                {isShowCloseButton ? (
                  <StyledClose
                    onClick={handleReset}
                    onMouseDown={event => event.stopPropagation()}
                  />
                ) : (
                  <StyledMagnifier
                    onClick={() => {
                      setCommittedSearchTerm(searchInput);
                      handleSearch(searchInput);
                    }}
                    onMouseDown={event => event.stopPropagation()}
                  />
                )}
              </StyledInputAdornment>
            }
            placeholder="Search SCMP"
            slotProps={{
              input: {
                ...parameters.inputProps,
              },
            }}
            slots={{ input }}
          />
        )}
        renderOption={renderOption}
        slotProps={{
          paper: {
            style: {
              border: suggestions.length > 0 ? "1px solid #e5e5e5" : "none",
              borderRadius: 0,
              boxShadow: "none",
            },
          },
          popper: {
            popperOptions: {
              modifiers: popperModifiers,
              placement: "bottom",
            },
          },
        }}
        value={committedSearchTerm}
      />
    </Container>
  );
};
